import { Button, Card, CardFooter, MessageBar, MessageBarBody, Text, Title3 } from "@fluentui/react-components";
import { MailMultiple24Regular } from '@fluentui/react-icons';
import type { ReactNode } from 'react';
import { MultiMailArchiveModeOptions } from './MultiMailArchiveModeOptions';
import SpinnerComponent from '../controls/spinner';

type ClassNames = {
    root: string;
    header: string;
    headerTitle: string;
    card: string;
    cardTextGroup: string;
    archiveOptions: string;
    noteText: string;
    footer: string;
};

type ArchiveChoiceText = {
    remainingOptionText: string;
    allOptionText: string;
    actionButtonText: string;
};

type TextProps = {
    title: string;
    description?: string;
};

type DisabledViewProps = {
    classNames: ClassNames;
    text: TextProps;
};

export const MultiMailArchiveDisabledView = ({ classNames, text }: DisabledViewProps) => {
    return (
        <div className={classNames.root}>
            <div className={classNames.header}>
                <Title3>{text.title}</Title3>
            </div>
        </div>
    );
};

type LoadingViewProps = {
    classNames: ClassNames;
    title: string;
    loadingLabel: string;
};

export const MultiMailArchiveLoadingView = ({ classNames, title, loadingLabel }: LoadingViewProps) => {
    return (
        <div className={classNames.root}>
            <div className={classNames.header}>
                <Title3 className={classNames.headerTitle}>
                    <MailMultiple24Regular /> {title}
                </Title3>
            </div>
            <SpinnerComponent showSpinner={true} size='medium' label={loadingLabel} labelPosition='after' />
        </div>
    );
};

type SkippedBarProps = {
    message: string | null;
};

export const MultiMailArchiveSkippedBar = ({ message }: SkippedBarProps) => {
    if (!message) {
        return null;
    }

    return (
        <MessageBar intent="warning">
            <MessageBarBody>{message}</MessageBarBody>
        </MessageBar>
    );
};

type AllArchivedViewProps = {
    classNames: ClassNames;
    text: {
        title: string;
        summary: string;
        description: string;
        archiveAllButtonText: string;
        showDocumentsButtonText?: string;
    };
    skippedMailsBar: ReactNode;
    archivedInfoContent: ReactNode;
    onArchiveMails: () => void;
    onShowDocuments?: () => void;
};

export const MultiMailArchiveAllArchivedView = ({
    classNames,
    text,
    skippedMailsBar,
    archivedInfoContent,
    onArchiveMails,
    onShowDocuments,
}: AllArchivedViewProps) => {
    return (
        <div className={classNames.root}>
            <div className={classNames.header}>
                <Title3>
                    <MailMultiple24Regular /> {text.title}
                </Title3>
            </div>
            {skippedMailsBar}
            <Card className={classNames.card}>
                <div className={classNames.cardTextGroup}>
                    <Text weight="semibold">{text.summary}</Text>
                    <Text>{text.description}</Text>
                </div>
                <CardFooter className={classNames.footer}>
                    <Button appearance="primary" onClick={onArchiveMails}>
                        {text.archiveAllButtonText}
                    </Button>
                </CardFooter>
            </Card>
            {onShowDocuments && text.showDocumentsButtonText && (
                <Button appearance="primary" onClick={onShowDocuments}>
                    {text.showDocumentsButtonText}
                </Button>
            )}
            {archivedInfoContent}
        </div>
    );
};



type ReadyViewProps = {
    classNames: ClassNames;
    text: {
        title: string;
        headerDescription: string;
        cardDescription: string;
        noteText?: string;
        archiveChoice: ArchiveChoiceText;
        showDocumentsButtonText?: string;
    };
    showArchiveModeChoice: boolean;
    selectedArchiveMode: string;
    onArchiveModeChange: (value: string) => void;
    skippedMailsBar: ReactNode;
    archivedInfoContent: ReactNode;
    onArchiveMails: () => void;
    onShowDocuments?: () => void;
};

export const MultiMailArchiveReadyView = ({
    classNames,
    text,
    showArchiveModeChoice,
    selectedArchiveMode,
    onArchiveModeChange,
    skippedMailsBar,
    archivedInfoContent,
    onArchiveMails,
    onShowDocuments,
}: ReadyViewProps) => {
    return (
        <div className={classNames.root}>
            <div className={classNames.header}>
                <Title3 className={classNames.headerTitle}>
                    <MailMultiple24Regular /> {text.title}
                </Title3>
                {onShowDocuments && text.showDocumentsButtonText && (
                    <Button appearance="secondary" onClick={onShowDocuments}>
                        {text.showDocumentsButtonText}
                    </Button>
                )}
            </div>
            {skippedMailsBar}
            <Card className={classNames.card}>
                <div className={classNames.cardTextGroup}>
                    <Text weight="semibold">{text.headerDescription}</Text>
                    <Text>{text.cardDescription}</Text>
                </div>
                {showArchiveModeChoice && (
                    <MultiMailArchiveModeOptions
                        className={classNames.archiveOptions}
                        text={text.archiveChoice}
                        selectedArchiveMode={selectedArchiveMode}
                        onArchiveModeChange={onArchiveModeChange}
                    />
                )}
                {text.noteText && (
                    <Text className={classNames.noteText}>{text.noteText}</Text>
                )}
                <CardFooter className={classNames.footer}>
                    <Button appearance="primary" onClick={onArchiveMails}>
                        {text.archiveChoice.actionButtonText}
                    </Button>
                </CardFooter>
            </Card>
            {archivedInfoContent}
        </div>
    );
};
